import mixpanel from "mixpanel-browser";

const token = import.meta.env.VITE_APP_MIXPANEL_TOKEN as string | undefined;

let initialized = false;

function ensureInitialized() {
  if (!token || initialized) return;

  mixpanel.init(token, {
    debug: import.meta.env.DEV,
    ignore_dnt: true,
    api_host: "https://api-eu.mixpanel.com",
    // Preview deployments live under a public suffix (e.g. *.netlify.app),
    // so Mixpanel's default cross-subdomain cookie attempt gets rejected by
    // the browser as an invalid cookie domain. Scope to the exact host.
    cross_subdomain_cookie: false,
  });
  mixpanel.register({ app: "weblinks" });
  initialized = true;
}

// Resolves campaign-id from the URL if present (registering it as a Mixpanel
// super property so it rides on every future event), otherwise falls back to
// whatever Mixpanel already has registered from earlier in this browser.
// No storage of our own — Mixpanel's own persistence is the only source.
export function getCampaignId(): string | null {
  if (!token) return null;

  ensureInitialized();

  const fromUrl = new URLSearchParams(window.location.search).get(
    "campaign-id",
  );
  if (fromUrl) {
    mixpanel.register({ campaign_id: fromUrl });
    return fromUrl;
  }

  return (mixpanel.get_property("campaign_id") as string | undefined) ?? null;
}

// Reads campaign-id off the URL as early as possible (app boot, before any
// client-side navigation can drop the query param) so Mixpanel already has
// it registered by the time a route that fires Mixpanel events mounts.
export function captureCampaignId() {
  getCampaignId();
}

// Registers the active partner's subdomain as a Mixpanel super property so
// it rides on every event automatically (landing view, identify, hmo id
// validated, consultation created) without each call site repeating it.
export function setPartnerSubdomain(subdomain: string) {
  if (!token) return;
  ensureInitialized();
  mixpanel.register({ subdomain });
}

// Clears campaign_id once a consultation has been created, so it doesn't
// keep attaching to unrelated activity later in the same browser (e.g. the
// user hits back and starts a second, unrelated consultation).
export function clearCampaignId() {
  if (!initialized) return;
  mixpanel.unregister("campaign_id");
}

function getMixpanelIfReady() {
  if (!token) {
    if (import.meta.env.DEV) {
      console.warn(
        "[Mixpanel] VITE_APP_MIXPANEL_TOKEN is not set (restart the dev server after editing .env)",
      );
    }
    return null;
  }

  // Captures campaign-id if present so it rides on this and future events as
  // a super property, but tracking must not be gated on it — most traffic
  // has no campaign-id at all and should still be tracked.
  getCampaignId();

  return mixpanel;
}

type MixpanelIdentity = {
  hmoId: string | undefined;
  email: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
};

export function identifyMixpanelUser(identity: MixpanelIdentity) {
  try {
    const mp = getMixpanelIfReady();
    if (!mp) return;
    const { hmoId, email, firstName, lastName } = identity;
    mp.identify(hmoId || crypto.randomUUID());
    mp.people.set({
      $name: [firstName, lastName].filter(Boolean).join(" ") || undefined,
      $email: email,
      $first_name: firstName,
      $last_name: lastName,
      campaign_id: getCampaignId() ?? undefined,
    });
  } catch {
    console.warn("[Mixpanel] Failed to identify user");
  }
}

export function trackMixpanelEvent(
  name: string,
  data: Record<string, unknown>,
) {
  try {
    const mp = getMixpanelIfReady();
    if (!mp) return;
    mp.track(name, data);
  } catch (error) {
    console.warn("[Mixpanel] Failed to track event", error);
  }
}
